import { startTransition, useCallback, useEffect, useRef, useState } from "react";
import {
  fetchNonBilledReports,
  getCustomerData,
  getUserDivisionData
} from "Service/ApiService";

const useDebounce = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
};

const getUniqueCustomerOptions = (customerList = []) =>
  customerList.reduce((uniqueList, currentItem) => {
    const existingItem = uniqueList.find((item) => item.value === currentItem.partyCode.toString());

    if (!existingItem) {
      uniqueList.push({
        value: currentItem.partyCode.toString(),
        label: `${currentItem.partyName}-${currentItem.city}-${currentItem.partyCode.toString()}`
      });
    }

    return uniqueList;
  }, []);

const NonBilledReportsController = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isFromApp = localStorage.getItem("isFromApp") === "true";

  const [filters, setFilters] = useState({
    customer: null,
    division: null,
    fromDate: "",
    toDate: ""
  });
  const debouncedFilters = useDebounce(filters, 500);

  const [customerOptions, setCustomerOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);

  const [reportData, setReportData] = useState([]);
  const [grandTotal, setGrandTotal] = useState({
    total_rolls: 0,
    total_meters: 0
  });

  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingDivisions, setLoadingDivisions] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const filtersRef = useRef(filters);
  const requestIdRef = useRef(0);
  const dropdownsReadyRef = useRef(false);
  const initialFiltersSetRef = useRef(false);
  const lastSignatureRef = useRef("");

  const getAssignedCustomerCodes = useCallback(
    () => customerOptions.map((item) => item.value).join(","),
    [customerOptions]
  );

  const getAssignedDivisionCodes = useCallback(
    () => divisionOptions.map((item) => item.value).join(","),
    [divisionOptions]
  );

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    let active = true;

    const loadDropdowns = async () => {
      try {
        setLoadingCustomers(true);
        setLoadingDivisions(true);

        const [customerRes, divisionRes] = await Promise.all([
          getCustomerData(user.userCode, ""),
          getUserDivisionData(user.userCode)
        ]);

        if (!active) {
          return;
        }

        const mappedCustomers =
          customerRes?.success === "true" ? getUniqueCustomerOptions(customerRes.customerList || []) : [];

        const mappedDivisions = Array.isArray(divisionRes?.divisions)
          ? divisionRes.divisions.map((division) => ({
              label: `${division.divCode} - ${division.divName?.trim() || ""}`,
              value: division.divCode
            }))
          : [];

        setCustomerOptions(mappedCustomers);
        setDivisionOptions(mappedDivisions);
        dropdownsReadyRef.current = true;
      } catch (error) {
        console.error("Non billed dropdown load error:", error);
        setCustomerOptions([]);
        setDivisionOptions([]);
        dropdownsReadyRef.current = true;
      } finally {
        if (active) {
          setLoadingCustomers(false);
          setLoadingDivisions(false);
          setInitialLoading(false);
        }
      }
    };

    loadDropdowns();

    return () => {
      active = false;
    };
  }, [user.userCode]);

  useEffect(() => {
    if (!dropdownsReadyRef.current || initialFiltersSetRef.current) {
      return;
    }

    initialFiltersSetRef.current = true;
    const nextFilters = {
      customer: null,
      division: null,
      fromDate: "",
      toDate: ""
    };

    setFilters(nextFilters);
    filtersRef.current = nextFilters;
  }, [customerOptions, divisionOptions]);

  const handleFilterChange = (field) => (value) => {
    setLoadingReport(true);
    setReportData([]);
    setGrandTotal({
      total_rolls: 0,
      total_meters: 0
    });
    setFilters((prev) => ({
      ...prev,
      [field]: value || null
    }));
  };

  const handleDateChange = (field) => (event) => {
    const value = event?.target?.value || "";
    setLoadingReport(true);
    setReportData([]);
    setGrandTotal({
      total_rolls: 0,
      total_meters: 0
    });
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCustomerSearch = useCallback(
    async (inputValue) => {
      if (!inputValue || inputValue.length < 3) {
        return;
      }

      try {
        setLoadingCustomers(true);
        const response = await getCustomerData(user.userCode, inputValue);

        if (response?.success === "true") {
          setCustomerOptions(getUniqueCustomerOptions(response.customerList || []));
        }
      } catch (error) {
        console.error("Non billed customer search error:", error);
      } finally {
        setLoadingCustomers(false);
      }
    },
    [user.userCode]
  );

  const loadAllCustomers = useCallback(async () => {
    try {
      setLoadingCustomers(true);
      const response = await getCustomerData(user.userCode, "");

      if (response?.success === "true") {
        setCustomerOptions(getUniqueCustomerOptions(response.customerList || []));
      }
    } catch (error) {
      console.error("Non billed customer load error:", error);
    } finally {
      setLoadingCustomers(false);
    }
  }, [user.userCode]);

  const getSignature = useCallback((currentFilters) => {
    return [
      currentFilters.customer?.value || getAssignedCustomerCodes(),
      currentFilters.division?.value || getAssignedDivisionCodes(),
      currentFilters.fromDate || "",
      currentFilters.toDate || ""
    ].join("|");
  }, [getAssignedCustomerCodes, getAssignedDivisionCodes]);

  const getNonBilledReport = useCallback(async () => {
    const currentFilters = filtersRef.current;
    const requestId = ++requestIdRef.current;

    try {
      setLoadingReport(true);

      const response = await fetchNonBilledReports(
        user.userCode,
        currentFilters.customer?.value || getAssignedCustomerCodes(),
        currentFilters.division?.value || getAssignedDivisionCodes(),
        currentFilters.fromDate,
        currentFilters.toDate
      );

      if (requestId !== requestIdRef.current) {
        return;
      }

      if (response?.status) {
        startTransition(() => {
          setReportData(Array.isArray(response.data) ? response.data : []);
          setGrandTotal({
            total_rolls: Number(response?.grand_total?.total_rolls || 0),
            total_meters: Number(response?.grand_total?.total_meters || 0)
          });
        });
      } else {
        startTransition(() => {
          setReportData([]);
          setGrandTotal({
            total_rolls: 0,
            total_meters: 0
          });
        });
      }
    } catch (error) {
      if (requestId === requestIdRef.current) {
        console.error("Non billed report error:", error);
        startTransition(() => {
          setReportData([]);
          setGrandTotal({
            total_rolls: 0,
            total_meters: 0
          });
        });
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoadingReport(false);
      }
    }
  }, [getAssignedCustomerCodes, getAssignedDivisionCodes, user.userCode]);

  useEffect(() => {
    if (!initialFiltersSetRef.current) {
      return;
    }

    const signature = getSignature(debouncedFilters);
    if (lastSignatureRef.current === signature) {
      return;
    }

    lastSignatureRef.current = signature;
    filtersRef.current = debouncedFilters;
    getNonBilledReport();
  }, [debouncedFilters, getNonBilledReport, getSignature]);

  return {
    filters,
    customerOptions,
    divisionOptions,
    reportData,
    grandTotal,
    cloading: initialLoading,
    tableLoading: loadingReport,
    dropdownLoading: loadingCustomers || loadingDivisions,
    loadingCustomers,
    handleFilterChange,
    handleDateChange,
    handleCustomerSearch,
    loadAllCustomers,
    isFromApp
  };
};

export default NonBilledReportsController;
